import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {ReservationDriver} from "types/entities/ReservationDriver";
import AccessTickerDriverIcon from "pages/Front/Booking/BookingLogin/components/ReservationDetailComponents/AccessTickerDriverIcon";
import {driverName} from "utils/utility";
import FeatherIcon from "components/FeatherIcon";

interface CreditsBalanceDriverItemProps {
    reservationDriver: ReservationDriver;
    limit: number | null | undefined;
    onLimitChange: (newLimit: number | null) => void;
    onRemoveLimit: () => void;
    totalAvailable: number;
    isMainAccount?: boolean;
}

const DriverCreditLimit = (props: {currentLimit: number}) => {
    const {t} = useTranslation();

    return (
        <div className={"DriverCreditLimit p-3 flex flex-row gap-2 items-center"}>
            {props.currentLimit == 0 && <div className={"text-bold transform rotate-90"}>8</div>}
            <span>{t("No limit")}</span>
        </div>
    );
};

const CreditsBalanceDriverItem = ({reservationDriver, limit, onLimitChange, onRemoveLimit, totalAvailable, isMainAccount}: CreditsBalanceDriverItemProps) => {
    const {t} = useTranslation();
    const [isEditing, setIsEditing] = React.useState<boolean>(limit != null && limit > 0);
    const currentLimit = typeof limit === "number" ? limit : 0;

    const handleLimitClick = () => {
        setIsEditing(true);
        if (limit == null) {
            onLimitChange(4); // Default to 1 ride if setting limit for first time
        }
    };

    const handleDecrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentLimit > 0) {
            onLimitChange(Math.max(0, currentLimit - 4));
        }
    };

    const handleIncrement = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (currentLimit + 4 <= totalAvailable) {
            onLimitChange(currentLimit + 4);
        }
    };

    const getDriverLabel = () => {
        if (isMainAccount) return t("Main account");
        if (reservationDriver.person_type === "child") {
            const age = reservationDriver.driver?.age;
            return t("Child") + (age ? ` · ${age} J.` : "");
        }
        if (reservationDriver.is_invited) return t("Invited");
        return t("Guest");
    };

    useEffect(() => {
        setIsEditing(limit != null && limit > 0);
    }, [limit]);

    return (
        <div className="CreditsBalanceDriverItem bg-white rounded-xl border border-[#EEEEEE] p-4 flex flex-row items-center justify-between gap-4 mb-3">
            <div className="flex flex-row items-center justify-start gap-4 basis-[300px]">
                <div className="relative size-[40px] md:size-[50px] shrink-0 flex items-center">
                    <AccessTickerDriverIcon reservationDriver={reservationDriver} packageItem={null} isNotAbsolutePosition={true} />
                </div>
                <div className="flex flex-col ">
                    <span className="font-bold text-sm md:text-lg text-black leading-tight">
                        {reservationDriver.driver ? driverName(reservationDriver.driver) : reservationDriver.name}
                    </span>
                    <span className="text-xs md:text-sm text-black font-medium">{getDriverLabel()}</span>
                </div>
            </div>

            <div className="flex flex-row items-center justify-end gap-4 md:gap-8 grow">
                {!isEditing ? (
                    <div className={"flex items-center justify-center"}>
                        <DriverCreditLimit currentLimit={currentLimit} />
                        <button
                            type={"button"}
                            onClick={handleLimitClick}
                            className="text-black border font-bold border-mid-grey rounded-sm bg-white p-3 text-sm md:text-sm hover:text-arbo-red hover:border-arbo-red transition-colors"
                        >
                            {t("Set limit")}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="place-content-start grow hidden md:block text-xs md:text-sm text-gray-500 font-medium">
                            {/* Example range, should be dynamic if possible */}
                            {t("max. 3 - 16 Fahrten")}
                        </div>
                        <div className="flex flex-row items-center gap-2">
                            <button
                                type={"button"}
                                onClick={handleDecrement}
                                className="size-8 md:size-10 rounded-xl border border-black flex items-center justify-center hover:bg-gray-50 active:bg-gray-100"
                            >
                                <FeatherIcon name="minus" className="size-4 text-black" strokeWidth={3} />
                            </button>
                            <div className="flex flex-row items-baseline gap-1 min-w-[60px] justify-center">
                                <span className="font-bold text-sm md:text-lg text-black">{currentLimit}</span>
                                <span className="text-[10px] md:text-xs font-bold text-black uppercase">max.</span>
                            </div>
                            <button
                                type={"button"}
                                onClick={handleIncrement}
                                className="size-8 md:size-10 rounded-xl border border-black flex items-center justify-center hover:bg-gray-50 active:bg-gray-100"
                            >
                                <FeatherIcon name="plus" className="size-4 text-black" strokeWidth={3} />
                            </button>
                        </div>

                        <button
                            type={"button"}
                            onClick={e => {
                                e.stopPropagation();
                                setIsEditing(false);
                                onRemoveLimit();
                            }}
                            className="text-black border font-bold border-mid-grey rounded-sm bg-white p-3 text-sm md:text-sm hover:text-arbo-red hover:border-arbo-red transition-colors"
                        >
                            {t("Remove")}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditsBalanceDriverItem;
